import { Icon } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import type { FC } from "react";
import styled from "styled-components";
import { Panel, useReactFlow, useViewport } from "@xyflow/react";
import {
  StyledZoomButton,
  StyledZoomControl,
  StyledZoomPercentage,
} from "./FlowZoomControl.style";
import { FLOW_MIN_ZOOM, FLOW_MAX_ZOOM } from "./graph/constants";

const zoomDuration = (): number =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? 0 : 200;

const ZOOM_ICON_DIMENSION = { width: 12, height: 12 };

const StyledPanelZoomControl = styled(StyledZoomControl)`
  position: static;
`;

export const FlowZoomControl: FC = () => {
  const { zoomIn, zoomOut, zoomTo, fitView } = useReactFlow();
  const { zoom } = useViewport();

  const displayZoom = Math.round(zoom * 100);
  const canZoomIn = zoom < FLOW_MAX_ZOOM;
  const canZoomOut = zoom > FLOW_MIN_ZOOM;

  return (
    <Panel
      position="bottom-left"
      className="nopan"
      style={{ margin: 13 }}
      data-testid="zoom-panel"
    >
      <StyledPanelZoomControl data-testid="zoom-control">
        <StyledZoomButton
          type="button"
          aria-label={t("canvas.zoom-out")}
          data-testid="zoom-out"
          disabled={!canZoomOut}
          onClick={() => {
            void zoomOut({ duration: zoomDuration() });
          }}
        >
          <Icon
            name="minus"
            color="currentColor"
            dimension={ZOOM_ICON_DIMENSION}
          />
        </StyledZoomButton>
        <StyledZoomPercentage
          type="button"
          aria-label={t("canvas.zoom-reset")}
          data-testid="zoom-reset"
          onClick={() => {
            void zoomTo(1, { duration: zoomDuration() });
          }}
        >
          {displayZoom}%
        </StyledZoomPercentage>
        <StyledZoomButton
          type="button"
          aria-label={t("canvas.zoom-in")}
          data-testid="zoom-in"
          disabled={!canZoomIn}
          onClick={() => {
            void zoomIn({ duration: zoomDuration() });
          }}
        >
          <Icon
            name="plus"
            color="currentColor"
            dimension={ZOOM_ICON_DIMENSION}
          />
        </StyledZoomButton>
        <StyledZoomButton
          type="button"
          aria-label={t("canvas.fit-to-screen")}
          data-testid="zoom-fit"
          onClick={() => {
            void fitView({ duration: zoomDuration() });
          }}
        >
          <Icon
            name="expand-smaller"
            size="S"
            color="currentColor"
            dimension={ZOOM_ICON_DIMENSION}
          />
        </StyledZoomButton>
      </StyledPanelZoomControl>
    </Panel>
  );
};
FlowZoomControl.displayName = "FlowZoomControl";
